import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import './styling/InvoiceStatusSearchForm.css';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowLeftOutlined,
     BulbOutlined,
    CheckCircleOutlined,
    DownloadOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    LoadingOutlined,
    ReloadOutlined,
    SaveOutlined,
    UserOutlined,
} from '@ant-design/icons';
import {
    Badge,
    Button,
    Col,
    Collapse,
    ConfigProvider,
    Dropdown,
    Form,
    Menu,
    Modal,
    notification,
    Row,
    Space,
    Spin,
    Tabs,
    Tooltip,
    Typography,
    theme as antdTheme,
} from 'antd';
import { MUTATION_UPDATE_INVOICE_STATUS, QUERY_INVOICE_FILTERS, QUERY_INVOICE_STATUS } from './invotrackQueries';
import { EIMMonitoringGridRequest, ExportFilters } from './interfaces';
import InvoTrackServerSideDatasource from './InvoTrackServerSideDatasource';
import { invoiceListColumns, invoiceListDefaultColDef, removeFilterDuplicates } from './utils';
import InvoiceStatusSearchForm from './InvoiceStatusSearchForm';
import { exportMonitoringData } from './excelUtil';
import { AgGridReact } from 'ag-grid-react';
import { IAppComponentProps } from '../../components';
import client from '../../config/GraphQLApolloClient';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { getI18n } from '../../utils/Utils';
import moduleStyles from '../documents/styling/document.module.css';
import classnames from 'classnames';
import overviewStyles from './EIMDocumentOverview.module.scss';
import { PersonalizeGridView } from '../info-portal/components/grid/GridUtil';
import { ColDef, GridApi } from 'ag-grid-community';
import { CurrentInvoiceStatus } from '../../__generated__/types';
import { entitlementExists } from '../../main/entitlementService';
import { useLocation, useNavigate } from 'react-router-dom';
import { EIM_THEME_STORAGE_KEY } from './themePreference';

const OPERATIONAL_DATA_ORIGIN_KEY = 'eimOperationalDataOrigin';
const DEFAULT_OPERATIONAL_DATA_ORIGIN = '/eim-document-executive-dashboard-monitoring';
const ALLOWED_OPERATIONAL_DATA_ORIGINS = new Set([
    '/eim-document-dashboard-monitoring',
    '/eim-document-executive-dashboard-monitoring',
    '/eim-document-executive-dashboard-monitoring-v2',
    '/cfo-reporting-dashboard',
    '/cfo-compliance-report',
]);

type EIMProps = {
    env: String;
} & IAppComponentProps;

const InvoiceStatusView: FunctionComponent<EIMProps> = (props) => {
    const [loading, setLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [_flag, setFlag] = useState(false);
    const [exportFilter, setExportFilter] = useState<ExportFilters>(null);
    const [form] = Form.useForm();

    type ThemeMode = 'dark' | 'light';

    const [theme, setTheme] = useState<ThemeMode>(() => {
        const storedTheme = globalThis.localStorage.getItem(EIM_THEME_STORAGE_KEY);
        return storedTheme === 'light' ? 'light' : 'dark';
    });

    const antdAlgorithm = useMemo(
        () => (theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm),
        [theme]
    );

    const antdThemeConfig = useMemo(
        () => ({
            algorithm: antdAlgorithm,
            ...(theme === 'dark'
                ? {
                      token: {
                          colorText: '#f3f7ff',
                          colorTextPlaceholder: '#5d7090',
                      },
                      components: {
                          Select: {
                              colorText: '#f3f7ff',
                              optionSelectedColor: '#ffffff',
                              optionSelectedBg: 'rgba(20, 127, 110, 0.55)',
                              optionActiveBg: 'rgba(26, 50, 76, 0.9)',
                              selectorBg: 'rgba(16, 27, 43, 0.92)',
                              colorBgElevated: 'rgba(14, 25, 40, 0.97)',
                          },
                      },
                  }
                : {}),
        }),
        [antdAlgorithm, theme]
    );

    useEffect(() => {
        globalThis.localStorage.setItem(EIM_THEME_STORAGE_KEY, theme);
    }, [theme]);
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchFilters, setSearchFilters] = useState<EIMMonitoringGridRequest>(() => {
        const type = window.sessionStorage.getItem('type');
        const dashDirection = type === 'IN' || type === 'OUT' ? type : undefined;
        const partnerCountry = window.sessionStorage.getItem('partnerCountry') || undefined;
        const statusFilters = window.sessionStorage.getItem('statusFilters')
            ? JSON.parse(window.sessionStorage.getItem('statusFilters'))
            : undefined;
        const systemIds = window.sessionStorage.getItem('systemIds')
            ? JSON.parse(window.sessionStorage.getItem('systemIds'))
            : undefined;
        
        // Map dashboard filter types to actual status values used in the database
        // FAIL, PASS, OVERDUE, INTRANSIT, BUSN are the values we need to filter by
        const statusMap: { [key: string]: string[] } = {
            'FAIL': ['FAIL'],
            'PASS': ['PASS'],
            'OVERDUE': ['OVERDUE'],
            'INTRANSIT': ['INTRANSIT', 'BUSN', 'NONE'],
            'BUSN': ['BUSN'],
        };
        
        const dashStatus = statusMap[type] || undefined;
        
        return {
            limit: 2000,
            offset: 0,
            page: 0,
            dashBoardFilters: type || undefined,
            duplicate: type ? ['1'] : undefined,
            fromDate: sessionStorage.getItem('fromDate')
                ? dayjs(sessionStorage.getItem('fromDate').replaceAll('"', ''))
                : dayjs(),
            toDate: sessionStorage.getItem('toDate')
                ? dayjs(sessionStorage.getItem('toDate').replaceAll('"', ''))
                : dayjs(),
            process: window.sessionStorage.getItem('process')
                ? JSON.parse(window.sessionStorage.getItem('process'))
                : undefined,
            company: window.sessionStorage.getItem('company')
                ? JSON.parse(window.sessionStorage.getItem('company'))
                : undefined,
            group_name: window.sessionStorage.getItem('companyGroup')
                ? JSON.parse(window.sessionStorage.getItem('companyGroup'))
                : undefined,
            status: statusFilters || dashStatus,
            direction: dashDirection,
            partner_country: partnerCountry,
            sys_id: systemIds,
        };
    });

    const operationalDataOrigin = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const originFromQuery = searchParams.get('origin');
        const originFromState = (location.state as { operationalDataOrigin?: string } | null)?.operationalDataOrigin;
        const originFromSession = globalThis.sessionStorage.getItem(OPERATIONAL_DATA_ORIGIN_KEY);
        const candidateOrigin = originFromState ?? originFromQuery ?? originFromSession;

        if (candidateOrigin && ALLOWED_OPERATIONAL_DATA_ORIGINS.has(candidateOrigin)) {
            return candidateOrigin;
        }

        return DEFAULT_OPERATIONAL_DATA_ORIGIN;
    }, [location.search, location.state]);

    useEffect(() => {
        globalThis.sessionStorage.setItem(OPERATIONAL_DATA_ORIGIN_KEY, operationalDataOrigin);
    }, [operationalDataOrigin]);

    const [lastSearch, setLastSearch] = useState<EIMMonitoringGridRequest>(null);

    const gridView = useRef<PersonalizeGridView>(
        new PersonalizeGridView(
            'eim_doc_overview_monitoring_ag_grid_personalized_view',
            invoiceListColumns(props.intl, props.currentUser).map(item=> {return {...item, tooltipField: item.field}})
        )
    );

    const [gridOptions, _setGridOptions] = useState({
        defaultColDef: invoiceListDefaultColDef,
        columnDefs: gridView.current.getView(),
        cacheBlockSize: 2000,
        maxBlocksInCache: 10000,
        enableServerSideSorting: true,
        enableServerSideFilter: true,
    });

    const searchCounter = useRef(0);

    const gridApi = useRef<GridApi>(null);
    const gridColumnApi = useRef(null);

    const [updateStatusesMutation, { loading: updatingStatuses }] = useMutation(MUTATION_UPDATE_INVOICE_STATUS);

    const reload = () => setFlag((flag) => !flag);

    //useEffect clears the session storage items referenced here on exit from component
    useEffect(() => {
        return () => {
            sessionStorage.removeItem('type');
            sessionStorage.removeItem('fromDate');
            sessionStorage.removeItem('toDate');
            sessionStorage.removeItem('process');
            sessionStorage.removeItem('company');
            sessionStorage.removeItem('companyGroup');
            sessionStorage.removeItem('statusFilters');
            sessionStorage.removeItem('partnerCountry');
            sessionStorage.removeItem('systemIds');
        };
    }, []);

    const onFirstDataRendered = (params) => {
        setRowCount(params.api.getServerSideRowCount?.() ?? params.api.getModel().getRowCount());
        setLoading(false);
    };

    const onModelUpdated = (params) => {
        setRowCount(params.api.getServerSideRowCount?.() ?? params.api.getModel().getRowCount());
        searchCounter.current++;
        if (searchCounter.current > 1) {
            setLoading(false);
        }
    };

    const initAfterGridReady = async (params) => {
        try {
            const response = await client.query({ query: QUERY_INVOICE_FILTERS, fetchPolicy: 'no-cache' });
            const allMessagesFilters = response.data?.getAllMessagesFilters;
            setExportFilter(allMessagesFilters);
            const updatedColumns = gridView.current.getOriginalColumns().map((column) => {
                let filterValues = allMessagesFilters?.[column['field']];
                if (['process', 'status', 'm_format'].includes(column['field'])) {
                    filterValues = removeFilterDuplicates(filterValues);
                }
                return filterValues
                    ? { ...column, filterParams: { values: filterValues.map((f) => f.value) } }
                    : column;
            });

            gridApi.current = params.api;
            gridColumnApi.current = params.columnApi;
            params.api.sizeColumnsToFit();
            gridApi.current.setServerSideDatasource(
                new InvoTrackServerSideDatasource(
                    props.env,
                    gridOptions,
                    client,
                    searchFilters,
                    setLastSearch,
                    allMessagesFilters
                )
            );
            gridView.current.initPersonalizedView(params.api);

            gridView.current = new PersonalizeGridView(
                'eim_doc_overview_monitoring_ag_grid_personalized_view',
                updatedColumns
            );
            gridView.current.initPersonalizedView(gridApi.current);
            gridApi.current.setColumnDefs(gridView.current.getView());
            reload();
        } catch (error) {
            console.error(error);
        }
    };

    const onGridReady = (params) => {
        initAfterGridReady(params);
    };

    const onFilterChanged = (params) => {
        ['process', 'duplicate', 'm_interface', 'status', 'step_number', 'step', 'company', 'sys_id'].forEach(
            (item) => {
                params.api.getFilterInstance(item)?.refreshFilterValues();
            }
        );
    };

    const handleSearch = (values) => {
        const newSearchFilters = { ...searchFilters, ...values };
        setLoading(true);
        setSearchFilters(newSearchFilters);
        gridApi.current.setServerSideDatasource(
            new InvoTrackServerSideDatasource(
                props.env,
                gridOptions,
                client,
                newSearchFilters,
                setLastSearch,
                exportFilter
            )
        );
        searchCounter.current = 0;
    };

    const handleReset = () => {
        setSearchFilters({ limit: 100, offset: 0, page: 0 });
    };

    const getAllDataForExport = async () => {
        try {
            const allDataRequest = {
                ...lastSearch,
                limit: 10_000_000, // Set a very high limit to get all records
            };

            const response = await client.query({
                query: QUERY_INVOICE_STATUS,
                variables: {
                    env: props.env,
                    request: allDataRequest,
                },
                fetchPolicy: 'network-only',
            });

            return response?.data?.getEIMInvoices?.invoiceStatusesList || [];
        } catch (error) {
            console.error('Error fetching all data for export:', error);
            return [];
        }
    };

    const onExport = async (_: any) => {
        if (!form.getFieldValue('fromDate') || !form.getFieldValue('toDate')) {
            Modal.warning({
                title: getI18n('Warning-message', 'Export Document(s)', intl),
                content: getI18n(
                    'please-select-a-date-range-before-exporting',
                    'Please select a date range before exporting.',
                    intl
                ),
            });
            return;
        }

        try {
            setLoading(true);
            const allData = await getAllDataForExport();

            if (allData.length === 0) {
                Modal.warning({
                    title: getI18n('Warning-message', 'Export Document(s)', intl),
                    content: getI18n('no-data-to-export', 'No data available to export.', intl),
                });
            } else {
                const columns: ColDef[] = gridApi.current.getColumnDefs();
                await exportMonitoringData(
                    allData,
                    columns,
                    `${dayjs().format('YYYY-MM-DD HH:mm')}-eInvoicingMonitoringExport.xlsx`
                );
                notification.success({
                    message: intl.formatMessage({ id: 'export-success' }),
                    description: `Exported ${allData.length} records successfully.`,
                });
            }
        } catch (error) {
            notification.error({
                message: intl.formatMessage({ id: 'export-error' }),
                description: 'Failed to export data. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    // ------------------ Status Update Logic ------------------
    const handleBulkStatusUpdate = async () => {
        const api = gridApi.current;
        if (!api) return;
        // Retrieve all currently loaded rows in the viewport/cache
        const rows = api.getSelectedRows();

        const invalidSelection = rows.find((r) => r.status !== 'NONE' && r.status !== 'FAIL' && r.status !== 'OVERDUE');
        if (invalidSelection) {
            notification.warning({
                message: intl.formatMessage({ id: 'eim_update_invalid_selection' }),
            });
            return;
        }

        const candidates = rows.filter((r) => r.status === 'NONE' || r.status === 'FAIL' || r.status === 'OVERDUE');
        if (!candidates.length) {
            notification.warning({
                message: intl.formatMessage({ id: 'no-none-status-docs' }),
            });
            return;
        }

        Modal.confirm({
            title: intl.formatMessage({ id: 'confirm-status-update', defaultMessage: 'Confirm Status Update' }),
            icon: <ExclamationCircleOutlined />,
            content: intl.formatMessage({ id: 'confirm-status-update-body' }).replace('{count}', candidates.length.toString()),
            okText: intl.formatMessage({ id: 'ecap-capture-bmw-retail-outlet-choice-yes' }),
            cancelText: intl.formatMessage({ id: 'ecap-capture-bmw-retail-outlet-choice-no' }),
            onOk: async () => {
                try {
                    const listOfInvoices: CurrentInvoiceStatus[] = candidates.map((c) => {
                        return { objectId: c.log_obj_id, status: c.status, step_number: c.step_number };
                    });

                    if (!listOfInvoices.length) {
                        notification.error({ message: intl.formatMessage({ id: 'no-object-ids' }) });
                        return;
                    }
                    const { data } = await updateStatusesMutation({
                        variables: {
                            env: props.env,
                            request: {
                                listOfInvoices,
                                new_status: 'PASS',
                            },
                        },
                    });
                    const updated = data?.updateEIMInvoiceStatuses?.updatedRows || 0;
                    if (updated > 0) {
                        notification.success({
                            message: intl.formatMessage({ id: 'status-update-success' }),
                            description: intl
                                .formatMessage({ id: 'status-update-success-desc' })
                                .replace('{count}', updated),
                        });
                        // Refresh grid
                        api.refreshServerSide({ purge: true });
                    } else {
                        notification.warning({
                            message: intl.formatMessage({ id: 'status-update-nochange' }),
                        });
                    }
                } catch (e) {
                    notification.error({
                        message: intl.formatMessage({
                            id: 'status-update-error',
                            defaultMessage: 'Status update failed',
                        }),
                        description: e.message,
                    });
                }
            },
        });
    };

    const onSave = (_: any) => {
        gridView.current.savePersonalizedView();
        reload();
        notification.info({ message: intl.formatMessage({ id: 'report-result-preferences-saved' }) });
    };

    const onApplyPerson = (_: any) => {
        gridView.current.applyPersonalizedView();
    };

    const onReset = (_: any) => {
        gridApi.current.setFilterModel(null);
        gridView.current.reset();
    };

    const handleBackToExecutiveOverview = () => {
        navigate(operationalDataOrigin);
    };

        const handleToggleTheme = useCallback(() => {
            setTheme((previousTheme) => (previousTheme === 'dark' ? 'light' : 'dark'));
        }, []);

    const downIcon = () => <DownOutlined />;

    const menu = (
        <Menu>
            <Menu.Item key="3" disabled={!gridView.current.hasPersonalizedView()}>
                <UserOutlined onClick={onApplyPerson} />
                <a data-testid={'info-portal-personal'} onClick={onApplyPerson}>
                    {intl.formatMessage({ id: 'info-portal-personal', defaultMessage: 'Personal' })}
                </a>
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="4">
                <SaveOutlined onClick={onSave} />

                <a data-testid={'info-portal-save'} onClick={onSave}>
                    {intl.formatMessage({ id: 'info-portal-save', defaultMessage: 'Save' })}
                </a>
            </Menu.Item>
        </Menu>
    );

    return (
        <Spin spinning={loading} size={'large'} indicator={<LoadingOutlined style={{ fontSize: 50 }} />}>
            <ConfigProvider theme={antdThemeConfig}>
                <div
                    className={classnames('ag-theme-balham', overviewStyles.documentOverviewPage, {
                        [overviewStyles.documentOverviewPageLight]: theme === 'light',
                        [overviewStyles.themeLightOverride]: theme === 'light',
                        [overviewStyles.themeDarkOverride]: theme === 'dark',
                    })}>
                    <div className={overviewStyles.pageThemeToolbar}>
                        <Button
                            className={overviewStyles.themeToggleButton}
                            onClick={handleToggleTheme}
                            icon={<BulbOutlined />}
                            size={'middle'}>
                            {theme === 'dark' ? 'Switch to light view' : 'Switch to dark view'}
                        </Button>
                    </div>
                    <Tabs size={'small'} type={'card'}>
                    <Tabs.TabPane tab={intl.formatMessage({ id: 'eim-document-overview-monitoring-menu-item' })} key="search">

                        <div className={overviewStyles.filterSectionWrap}>
                            <Collapse defaultActiveKey={['document-monitoring-search-filters']}>
                                <Collapse.Panel key='document-monitoring-search-filters' header={getI18n('search-filters','Search Filters', intl)} >
                                    <InvoiceStatusSearchForm
                                        form={form}
                                        currentUser={props.currentUser}
                                        searchFilters={searchFilters}
                                        onSearch={handleSearch}
                                        onReset={handleReset}
                                        intl={intl}
                                    />
                                </Collapse.Panel>

                            </Collapse>
                        </div>
                        <div
                            className={classnames(overviewStyles.monitoringContainer, {
                                [overviewStyles.monitoringContainerLight]: theme === 'light',
                            })}>
                            <div className={overviewStyles.monitoringHeader}>
                                <div>
                                    <label>{getI18n('welcome-No_documents', '  Number Of Documents  ', intl)} </label>
                                    <Space>
                                        <Badge
                                            className="site-badge-count-109"
                                            count={rowCount}
                                            style={{ backgroundColor: '#52c41a' }}
                                            title={'Number of Documents'}
                                            overflowCount={999999}
                                        />
                                    </Space>
                                </div>

                                <Row gutter={24} style={{ paddingTop: '5px' }}>
                                    <Col style={{ paddingRight: '5px' }}>
                                        <Button
                                            data-testid={'back-to-executive-overview'}
                                            onClick={handleBackToExecutiveOverview}
                                            icon={<ArrowLeftOutlined />}
                                            size={'middle'}>
                                            {getI18n(
                                                'eim-back-to-executive-overview',
                                                'Back to previous page',
                                                intl
                                            )}
                                        </Button>
                                    </Col>
                                    <Col style={{ paddingRight: '5px' }}>
                                        <Dropdown.Button
                                            data-testid={'view-result-customization'}
                                            overlay={menu} trigger={['click']}
                                            icon={downIcon()} size={'middle'}>
                                            <EyeOutlined />
                                            {intl.formatMessage({
                                                id: 'info-portal-view',
                                                defaultMessage: 'View',
                                            })}
                                        </Dropdown.Button>
                                    </Col>

                                    <Col style={{ paddingRight: '5px' }}>
                                        <Tooltip
                                            title={intl.formatMessage({ id: 'reset-filter' })}>
                                            <Button
                                                data-testid={'view-result-customization-reset'}
                                                onClick={onReset} size={'middle'}
                                                icon={<ReloadOutlined
                                                    className={moduleStyles.simpleFlipRefresh} />}>
                                                {intl.formatMessage({ id: 'info-portal-reset' })}
                                            </Button>
                                        </Tooltip>
                                    </Col>
                                    <Col style={{ paddingRight: '5px' }}>
                                        <Tooltip
                                            title={intl.formatMessage({ id: 'welcome-download-button' })}>
                                            <Button data-testid={'result-download'}
                                                    onClick={onExport}
                                                    icon={<DownloadOutlined />}
                                                    size={'middle'}>
                                                {intl.formatMessage({ id: 'welcome-download-button' })}
                                            </Button>
                                        </Tooltip>
                                    </Col>
                                    <Col style={{ paddingRight: '5px' }} hidden={!entitlementExists('update@monitoring', props.distinctEntitlements)}>
                                        <Tooltip title={intl.formatMessage({ id: 'update-status-pass', defaultMessage: 'Set NONE -> PASS' })}>
                                            <Button
                                                type={'primary'}
                                                loading={updatingStatuses}
                                                onClick={handleBulkStatusUpdate}
                                                icon={<CheckCircleOutlined />}
                                                size={'middle'}>
                                                {intl.formatMessage({ id: 'update-status-pass-button' })}
                                            </Button>
                                        </Tooltip>
                                    </Col>
                                </Row>
                            </div>
                            <div className={classnames('ag-theme-balham', 'eim-document-overview', overviewStyles.monitoringGrid)}>
                                <AgGridReact
                                    key={'invoiceStatusListTable'}
                                    rowModelType={'serverSide'}
                                    onFirstDataRendered={onFirstDataRendered}
                                    columnDefs={gridView.current.getView()}
                                    rowSelection={'multiple'}
                                    onModelUpdated={onModelUpdated}
                                    paginationAutoPageSize={true}
                                    pagination={true}
                                    cacheBlockSize={gridOptions.cacheBlockSize}
                                    enableRangeSelection={true}
                                    onGridReady={onGridReady}
                                    defaultColDef={gridOptions.defaultColDef}
                                    animateRows={true}
                                    sideBar={true}
                                    onFilterChanged={onFilterChanged}
                                />
                            </div>
                        </div>
                    </Tabs.TabPane>
                    </Tabs>
                </div>
            </ConfigProvider>
        </Spin>
    );
};

export default InvoiceStatusView;
